import { request } from '@/api/apiUtils';
import { enrichCharacter } from '@/data/characterExtras';
import { createEntityQuery } from '@/data/createEntityQuery';
import type { EntityDefinition, FetcherEntry } from '@/data/types';
import type { ICharacter, IRelations, ResponseType } from '@/types';

/** Root: query-key prefix for this entity's slice of the cache (ADR 0010) */
export const CHARACTERS_ROOT = ['characters'] as const;

interface CharactersListParams {
    projectId: number;
    version?: string;
}

interface CharactersPresentationsParams {
    projectId: number;
    version?: string;
    tags?: string;
    query?: string;
    name?: string;
}

interface CharactersDetailParams {
    projectId: number;
    characterId: number;
    version?: string;
}

interface CharactersRelationsParams {
    projectId: number;
    characterId: number;
    version?: string;
}

const fetchers = {
    list: {
        key: ({ projectId, version }: CharactersListParams) => [...CHARACTERS_ROOT, 'list', { projectId, version }] as const,
        fetch: async ({ projectId, version }: CharactersListParams): Promise<ICharacter[]> => {
            const params = new URLSearchParams();
            if (version) params.append('version', version);
            const endpoint = `projects/${projectId}/characters${params.toString() ? `?${params}` : ''}`;
            const data: { data: ICharacter[] } = await request(endpoint);
            // Frozen-contract graft (characterExtras.ts): cache always holds
            // enriched characters; drops with the backend swap.
            return data.data.map(character => enrichCharacter(projectId, character));
        },
    },
    presentations: {
        key: (args: CharactersPresentationsParams) => [...CHARACTERS_ROOT, 'presentations', args] as const,
        fetch: async ({ projectId, version, tags, query, name }: CharactersPresentationsParams): Promise<ICharacter[]> => {
            const searchParams = new URLSearchParams();
            if (version) searchParams.set('version', version);
            if (query) searchParams.set('query', query);
            if (name) searchParams.set('name', name);
            const qs = searchParams.toString();
            const tagsStr = tags ?? '';
            const url = `projects/${projectId}/presentations/characters?${tagsStr}${tagsStr && qs ? '&' : ''}${qs}`;
            const response: ResponseType<ICharacter[]> = await request(url);
            return response.data.map(character => enrichCharacter(projectId, character));
        },
    },
    detail: {
        key: ({ projectId, characterId, version }: CharactersDetailParams) =>
            [...CHARACTERS_ROOT, 'detail', { projectId, characterId, version }] as const,
        fetch: async ({ projectId, characterId, version }: CharactersDetailParams): Promise<ICharacter> => {
            const params = new URLSearchParams();
            if (version) params.set('version', version);
            const qs = params.toString();
            const url = `projects/${projectId}/characters/${characterId}${qs ? `?${qs}` : ''}`;
            const response: ResponseType<ICharacter> = await request(url);
            return enrichCharacter(projectId, response.data);
        },
    },
    relations: {
        key: ({ projectId, characterId, version }: CharactersRelationsParams) =>
            [...CHARACTERS_ROOT, 'relations', { projectId, characterId, version }] as const,
        fetch: async ({ projectId, characterId, version }: CharactersRelationsParams): Promise<IRelations[]> => {
            const searchParams = new URLSearchParams();
            searchParams.set('character_id', String(characterId));
            if (version) searchParams.set('version', version);
            const response: ResponseType<IRelations[]> = await request(
                `projects/${projectId}/character-relations?${searchParams.toString()}`,
            );
            return response.data;
        },
    },
    // biome-ignore lint/suspicious/noExplicitAny: required for generic type inference
} satisfies Record<string, FetcherEntry<any, any>>;

export const charactersEntity: EntityDefinition<typeof fetchers> = {
    name: 'characters',
    roots: [CHARACTERS_ROOT],
    fetchers,
};

export const useCharactersList = createEntityQuery(charactersEntity, 'list');
export const useCharactersPresentations = createEntityQuery(charactersEntity, 'presentations');
export const useCharactersDetail = createEntityQuery(charactersEntity, 'detail');
export const useCharactersRelations = createEntityQuery(charactersEntity, 'relations');

export type { CharactersDetailParams, CharactersListParams, CharactersPresentationsParams, CharactersRelationsParams };
